import { SlIcon } from "@shoelace-style/shoelace/dist/react";
import { useEffect, useRef, useState,useCallback } from "react";

export default function ImageAutoCropper({ handlePrevious,handleNext,filterData,setCroppedCanvas ,setOpenEditModal,imageUrl}) {
  // Canvas refs
  const canvasRef = useRef(null);
  const resultCanvasRef = useRef(null);

  // State management
  const [image, setImage] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selection, setSelection] = useState(null);
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [adjustHandle, setAdjustHandle] = useState(null);
  const [adjustStart, setAdjustStart] = useState({ x: 0, y: 0 });
  const [drawStart, setDrawStart] = useState({ x: 0, y: 0 });
  const [drawEnd, setDrawEnd] = useState({ x: 0, y: 0 });
  const [manualMode, setManualMode] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [cursor, setCursor] = useState("crosshair");
  const [showResult, setShowResult] = useState(false);
  const [imageDimensions, setImageDimensions] = useState("-");
  const [imageType, setImageType] = useState("-");
  const [detectionStatus, setDetectionStatus] = useState("-");
  const [bordersFound, setBordersFound] = useState("-");
  // Add this with your other useState declarations
  const [firstPoint, setFirstPoint] = useState(null); // { x, y } or null
  // Detection parameters
  const [detectionParams, setDetectionParams] = useState({
    whiteThreshold: 230,
    whiteAreaMin: 8,
    maxSearchDistancePercent: 70,
    scanRange: 50,
  });

  // Load image when imageUrl changes
  useEffect(() => {
    if (imageUrl) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        setImage(img);
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
        }
      };
      img.src = imageUrl;
    }
  }, [imageUrl]);

  // Redraw canvas whenever selection, image, or drawing state changes
  useEffect(() => {
    redraw();
  }, [selection, image, isDrawing, drawStart, drawEnd]);

  // Helper functions
  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = Math.floor((e.clientX - rect.left) * scaleX);
    const y = Math.floor((e.clientY - rect.top) * scaleY);
    return { x, y };
  };

  const isPointInSelection = (x, y) => {
    if (!selection) return false;
    return x >= selection.x && x <= selection.x + selection.width && y >= selection.y && y <= selection.y + selection.height;
  };

  const getHandleAt = (x, y) => {
    if (!selection) return null;
    const handleSize = 8;
    const handles = {
      nw: { x: selection.x, y: selection.y },
      ne: { x: selection.x + selection.width, y: selection.y },
      sw: { x: selection.x, y: selection.y + selection.height },
      se: { x: selection.x + selection.width, y: selection.y + selection.height },
      n: { x: selection.x + selection.width / 2, y: selection.y },
      s: { x: selection.x + selection.width / 2, y: selection.y + selection.height },
      w: { x: selection.x, y: selection.y + selection.height / 2 },
      e: { x: selection.x + selection.width, y: selection.y + selection.height / 2 },
    };

    for (const [handle, pos] of Object.entries(handles)) {
      const dx = x - pos.x;
      const dy = y - pos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance <= handleSize) {
        return handle;
      }
    }
    return null;
  };

  const updateCursor = (x, y) => {
    if (!selection) {
      setCursor("crosshair");
      return;
    }

    const handle = getHandleAt(x, y);
    if (handle) {
      const cursors = {
        nw: "nwse-resize",
        ne: "nesw-resize",
        sw: "nesw-resize",
        se: "nwse-resize",
        n: "ns-resize",
        s: "ns-resize",
        w: "ew-resize",
        e: "ew-resize",
      };
      setCursor(cursors[handle] || "crosshair");
    } else if (isPointInSelection(x, y)) {
      setCursor("move");
    } else {
      setCursor("crosshair");
    }
  };

  const detectBorders = (clickX, clickY) => {
    const canvas = canvasRef.current;
    if (!image || !canvas) return null;

    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const width = canvas.width;
    const height = canvas.height;

    if (clickX < 0 || clickX >= width || clickY < 0 || clickY >= height) {
      return null;
    }

    const whiteThreshold = detectionParams.whiteThreshold;
    const whiteAreaMin = detectionParams.whiteAreaMin;
    const maxSearchDistance = Math.min(width, height) * (detectionParams.maxSearchDistancePercent / 100);
    const minSize = 50;
    const scanRange = detectionParams.scanRange;

    if (debugMode) {
      console.log("=== Border Detection Debug ===");
      console.log("Click position:", { x: clickX, y: clickY });
      setDetectionStatus(`Searching from (${Math.round(clickX)}, ${Math.round(clickY)})...`);
    }

    const getIntensity = (x, y) => {
      if (x < 0 || x >= width || y < 0 || y >= height) return 255;
      const idx = (Math.floor(y) * width + Math.floor(x)) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      return (r + g + b) / 3;
    };

    const isWhite = (x, y) => {
      return getIntensity(x, y) >= whiteThreshold;
    };

    const hasWhiteBorderArea = (startX, startY, dirX, dirY, perpDirX, perpDirY) => {
      let whiteCount = 0;
      let totalChecked = 0;
      let continuousWhite = 0;
      let maxContinuousWhite = 0;

      for (let offset = -scanRange / 2; offset <= scanRange / 2; offset += 1) {
        const x = Math.floor(startX + perpDirX * offset);
        const y = Math.floor(startY + perpDirY * offset);

        if (x < 0 || x >= width || y < 0 || y >= height) continue;

        for (let i = 0; i < 10; i++) {
          const checkX = Math.floor(startX + dirX * i + perpDirX * offset);
          const checkY = Math.floor(startY + dirY * i + perpDirY * offset);
          if (checkX >= 0 && checkX < width && checkY >= 0 && checkY < height) {
            if (isWhite(checkX, checkY)) {
              whiteCount++;
              continuousWhite++;
            } else {
              if (continuousWhite > maxContinuousWhite) {
                maxContinuousWhite = continuousWhite;
              }
              continuousWhite = 0;
            }
            totalChecked++;
          }
        }
        if (continuousWhite > maxContinuousWhite) {
          maxContinuousWhite = continuousWhite;
        }
      }

      const whiteRatio = totalChecked > 0 ? whiteCount / totalChecked : 0;
      return whiteRatio > 0.7 || maxContinuousWhite >= whiteAreaMin;
    };

    const findVerticalBorder = (startX, startY, direction) => {
      const step = direction > 0 ? 1 : -1;
      let bestWhiteBorderX = null;
      let bestWhiteStrength = 0;
      let consecutiveWhite = 0;
      let whiteBorderFound = false;

      for (let i = 0; i < maxSearchDistance; i++) {
        const x = Math.floor(startX + step * i);
        if (x < 0 || x >= width) break;

        const hasWhite = hasWhiteBorderArea(x, startY, step, 0, 0, 1);

        if (hasWhite) {
          consecutiveWhite++;

          let whiteStrength = 0;
          let sampleCount = 0;
          const sampleRange = Math.min(80, height / 3);

          for (let sy = -sampleRange; sy <= sampleRange; sy += 1) {
            const y = Math.floor(startY + sy);
            if (y >= 0 && y < height) {
              for (let dx = -3; dx <= 3; dx++) {
                const checkX = Math.floor(x + dx);
                if (checkX >= 0 && checkX < width && isWhite(checkX, y)) {
                  whiteStrength++;
                }
                sampleCount++;
              }
            }
          }

          const strength = sampleCount > 0 ? whiteStrength / sampleCount : 0;

          if (strength > bestWhiteStrength) {
            bestWhiteStrength = strength;
            bestWhiteBorderX = x;
          }

          if (consecutiveWhite >= whiteAreaMin && strength > 0.6) {
            whiteBorderFound = true;
            break;
          }
        } else {
          if (bestWhiteBorderX !== null && bestWhiteStrength > 0.6) {
            whiteBorderFound = true;
            break;
          }
          consecutiveWhite = 0;
        }
      }

      if (whiteBorderFound && bestWhiteBorderX !== null) {
        return bestWhiteBorderX;
      }

      for (let i = 0; i < maxSearchDistance; i++) {
        const x = Math.floor(startX + step * i);
        if (x < 0 || x >= width) break;

        let whitePixels = 0;
        let totalPixels = 0;
        for (let sy = -scanRange / 2; sy <= scanRange / 2; sy += 1) {
          const y = Math.floor(startY + sy);
          if (y >= 0 && y < height) {
            const intensity = getIntensity(x, y);
            if (intensity >= whiteThreshold) {
              whitePixels++;
            }
            totalPixels++;
          }
        }

        if (totalPixels > 0 && whitePixels / totalPixels > 0.75) {
          return x;
        }
      }

      return direction > 0 ? Math.min(startX + maxSearchDistance, width - 1) : Math.max(startX - maxSearchDistance, 0);
    };

    const findHorizontalBorder = (startX, startY, direction) => {
      const step = direction > 0 ? 1 : -1;
      let prevWasContent = false;
      let bestTransitionY = null;
      let bestTransitionStrength = 0;
      const sampleRange = Math.min(100, width / 2);

      for (let i = 0; i < maxSearchDistance; i++) {
        const y = Math.floor(startY + step * i);
        if (y < 0 || y >= height) break;

        let whitePixels = 0;
        let contentPixels = 0;
        let totalPixels = 0;

        for (let sx = -sampleRange; sx <= sampleRange; sx += 1) {
          const x = Math.floor(startX + sx);
          if (x >= 0 && x < width) {
            const intensity = getIntensity(x, y);
            if (intensity >= whiteThreshold) {
              whitePixels++;
            } else {
              contentPixels++;
            }
            totalPixels++;
          }
        }

        if (totalPixels === 0) continue;

        const whiteRatio = whitePixels / totalPixels;
        const contentRatio = contentPixels / totalPixels;
        const currentIsWhite = whiteRatio > 0.7;
        const currentIsContent = contentRatio > 0.3;

        if (prevWasContent && currentIsWhite) {
          let transitionStrength = 0;
          let transitionCount = 0;

          for (let sx = -sampleRange; sx <= sampleRange; sx += 2) {
            const x = Math.floor(startX + sx);
            if (x >= 0 && x < width) {
              const beforeY = y - step;
              const afterY = y;

              if (beforeY >= 0 && beforeY < height && afterY >= 0 && afterY < height) {
                const beforeIntensity = getIntensity(x, beforeY);
                const afterIntensity = getIntensity(x, afterY);

                if (beforeIntensity < whiteThreshold && afterIntensity >= whiteThreshold) {
                  transitionStrength++;
                }
                transitionCount++;
              }
            }
          }

          const strength = transitionCount > 0 ? transitionStrength / transitionCount : 0;

          if (strength > bestTransitionStrength) {
            bestTransitionStrength = strength;
            bestTransitionY = y;
          }

          if (strength > 0.5) {
            return y;
          }
        }

        prevWasContent = currentIsContent;
      }

      if (bestTransitionY !== null && bestTransitionStrength > 0.3) {
        return bestTransitionY;
      }

      for (let i = 0; i < maxSearchDistance; i++) {
        const y = Math.floor(startY + step * i);
        if (y < 0 || y >= height) break;

        let whitePixels = 0;
        let totalPixels = 0;
        for (let sx = -scanRange / 2; sx <= scanRange / 2; sx += 1) {
          const x = Math.floor(startX + sx);
          if (x >= 0 && x < width) {
            const intensity = getIntensity(x, y);
            if (intensity >= whiteThreshold) {
              whitePixels++;
            }
            totalPixels++;
          }
        }

        if (totalPixels > 0 && whitePixels / totalPixels > 0.75) {
          return y;
        }
      }

      return direction > 0 ? Math.min(startY + maxSearchDistance, height - 1) : Math.max(startY - maxSearchDistance, 0);
    };

    const leftBorder = findVerticalBorder(clickX, clickY, -1);
    const rightBorder = findVerticalBorder(clickX, clickY, 1);
    const topBorder = findHorizontalBorder(clickX, clickY, -1);
    const bottomBorder = findHorizontalBorder(clickX, clickY, 1);

    if (debugMode) {
      console.log("Detected Borders:", {
        left: Math.round(leftBorder),
        right: Math.round(rightBorder),
        top: Math.round(topBorder),
        bottom: Math.round(bottomBorder),
      });
      setBordersFound(`Left: ${Math.round(leftBorder)}, Right: ${Math.round(rightBorder)}, Top: ${Math.round(topBorder)}, Bottom: ${Math.round(bottomBorder)}`);
    }

    const x = Math.max(0, Math.min(leftBorder, rightBorder));
    const y = Math.max(0, Math.min(topBorder, bottomBorder));
    let w = Math.abs(rightBorder - leftBorder);
    let h = Math.abs(bottomBorder - topBorder);

    w = Math.max(minSize, Math.min(w, width - x));
    h = Math.max(minSize, Math.min(h, height - y));

    const finalX = Math.max(0, Math.min(x, width - 1));
    const finalY = Math.max(0, Math.min(y, height - 1));
    const finalW = Math.min(w, width - finalX);
    const finalH = Math.min(h, height - finalY);

    if (finalW < minSize || finalH < minSize) {
      const defaultSize = 250;
      const fallback = {
        x: Math.max(0, Math.min(clickX - defaultSize / 2, width - defaultSize)),
        y: Math.max(0, Math.min(clickY - defaultSize / 2, height - defaultSize)),
        width: Math.min(defaultSize, width - Math.max(0, clickX - defaultSize / 2)),
        height: Math.min(defaultSize, height - Math.max(0, clickY - defaultSize / 2)),
      };
      if (debugMode) {
        console.warn("Detection too small, using default size");
        setDetectionStatus("Warning: Detection too small, using default size");
      }
      return fallback;
    }

    const result = { x: finalX, y: finalY, width: finalW, height: finalH };

    if (debugMode) {
      console.log("Final Selection:", result);
      setDetectionStatus(`Success! Found: ${Math.round(finalW)}×${Math.round(finalH)} at (${Math.round(finalX)}, ${Math.round(finalY)})`);
    }

    return result;
  };

  const adjustRectangle = (handle, deltaX, deltaY, currentSelection) => {
    const canvas = canvasRef.current;
    if (!canvas) return currentSelection;

    const minSize = 20;
    const newSelection = { ...currentSelection };

    switch (handle) {
      case "nw":
        newSelection.x = Math.max(0, Math.min(newSelection.x + deltaX, newSelection.x + newSelection.width - minSize));
        newSelection.y = Math.max(0, Math.min(newSelection.y + deltaY, newSelection.y + newSelection.height - minSize));
        newSelection.width = Math.max(minSize, newSelection.width - deltaX);
        newSelection.height = Math.max(minSize, newSelection.height - deltaY);
        break;
      case "ne":
        newSelection.y = Math.max(0, Math.min(newSelection.y + deltaY, newSelection.y + newSelection.height - minSize));
        newSelection.width = Math.max(minSize, Math.min(newSelection.width + deltaX, canvas.width - newSelection.x));
        newSelection.height = Math.max(minSize, newSelection.height - deltaY);
        break;
      case "sw":
        newSelection.x = Math.max(0, Math.min(newSelection.x + deltaX, newSelection.x + newSelection.width - minSize));
        newSelection.width = Math.max(minSize, newSelection.width - deltaX);
        newSelection.height = Math.max(minSize, Math.min(newSelection.height + deltaY, canvas.height - newSelection.y));
        break;
      case "se":
        newSelection.width = Math.max(minSize, Math.min(newSelection.width + deltaX, canvas.width - newSelection.x));
        newSelection.height = Math.max(minSize, Math.min(newSelection.height + deltaY, canvas.height - newSelection.y));
        break;
      case "n":
        newSelection.y = Math.max(0, Math.min(newSelection.y + deltaY, newSelection.y + newSelection.height - minSize));
        newSelection.height = Math.max(minSize, newSelection.height - deltaY);
        break;
      case "s":
        newSelection.height = Math.max(minSize, Math.min(newSelection.height + deltaY, canvas.height - newSelection.y));
        break;
      case "w":
        newSelection.x = Math.max(0, Math.min(newSelection.x + deltaX, newSelection.x + newSelection.width - minSize));
        newSelection.width = Math.max(minSize, newSelection.width - deltaX);
        break;
      case "e":
        newSelection.width = Math.max(minSize, Math.min(newSelection.width + deltaX, canvas.width - newSelection.x));
        break;
      default:
        break;
    }

    return newSelection;
  };

  const redraw = () => {
    const canvas = canvasRef.current;
    if (!image || !canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0);

    // Draw temporary selection while dragging in manual mode
    // In manual mode: show live preview rectangle after first click
    if (manualMode && firstPoint && !selection) {
      ctx.strokeStyle = "#ff0000";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 6]);

      const width = drawEnd.x - firstPoint.x;
      const height = drawEnd.y - firstPoint.y;
      ctx.strokeRect(firstPoint.x, firstPoint.y, width, height);
    }

    // Draw final selection
    if (selection && selection.width > 0 && selection.height > 0) {
      // Draw blue rectangle border
      ctx.strokeStyle = "#0000ff";
      ctx.lineWidth = 6;
      ctx.setLineDash([]);
      ctx.strokeRect(selection.x, selection.y, selection.width, selection.height);

      // Draw corner handles
      const handles = [
        { x: selection.x, y: selection.y },
        { x: selection.x + selection.width, y: selection.y },
        { x: selection.x, y: selection.y + selection.height },
        { x: selection.x + selection.width, y: selection.y + selection.height },
        { x: selection.x + selection.width / 2, y: selection.y },
        { x: selection.x + selection.width / 2, y: selection.y + selection.height },
        { x: selection.x, y: selection.y + selection.height / 2 },
        { x: selection.x + selection.width, y: selection.y + selection.height / 2 },
      ];

      ctx.fillStyle = "#ff0000";
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 6;

      handles.forEach((handle) => {
        ctx.beginPath();
        ctx.arc(handle.x, handle.y, 6, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
      });
    }
  };

  const detectImageType = (canvas) => {
    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    let colorPixels = 0;
    let totalPixels = 0;
    const sampleSize = Math.min(10000, data.length / 4);
    const step = Math.max(1, Math.floor(data.length / 4 / sampleSize));

    for (let i = 0; i < data.length; i += step * 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      const tolerance = 10;
      const isColor = Math.abs(r - g) > tolerance || Math.abs(g - b) > tolerance || Math.abs(r - b) > tolerance;

      if (isColor) {
        colorPixels++;
      }
      totalPixels++;
    }

    const colorPercentage = (colorPixels / totalPixels) * 100;
    return colorPercentage > 5 ? "Color" : "Black & White";
  };

  // Event Handlers
  const handleMouseDown = (e) => {
    if (!image) return;

    const { x, y } = getCanvasCoordinates(e);

    // If there's already a selection → allow resize/move (unchanged logic)
    if (selection) {
      const handle = getHandleAt(x, y);
      if (handle) {
        setIsAdjusting(true);
        setAdjustHandle(handle);
        setAdjustStart({ x, y });
        return;
      }
      if (isPointInSelection(x, y)) {
        setIsAdjusting(true);
        setAdjustHandle("move");
        setAdjustStart({ x: x - selection.x, y: y - selection.y });
        return;
      }
    }

    // MANUAL MODE: two-click selection
    if (manualMode) {
      if (!firstPoint) {
        // First click — remember the starting corner
        setFirstPoint({ x, y });
        setSelection(null); // clear previous selection
      } else {
        // Second click — create rectangle from the two points
        const x1 = Math.min(firstPoint.x, x);
        const y1 = Math.min(firstPoint.y, y);
        const width = Math.abs(x - firstPoint.x);
        const height = Math.abs(y - firstPoint.y);

        if (width > 5 && height > 5) {
          setSelection({ x: x1, y: y1, width, height });
        }

        setFirstPoint(null); // reset for next manual crop
      }
    } else {
      // Auto-detect mode (unchanged)
      const detectedBounds = detectBorders(x, y);
      if (detectedBounds && detectedBounds.width > 0 && detectedBounds.height > 0) {
        setSelection(detectedBounds);
      }
    }
  };

  const handleMouseMove = (e) => {
    if (!image) return;

    const { x, y } = getCanvasCoordinates(e);

    // Live preview when waiting for second click in manual mode
    if (manualMode && firstPoint && !selection) {
      setDrawStart(firstPoint);
      setDrawEnd({ x, y });
    }
    // Resizing / moving existing selection
    else if (isAdjusting && selection) {
      const deltaX = x - adjustStart.x;
      const deltaY = y - adjustStart.y;

      if (adjustHandle === "move") {
        const canvas = canvasRef.current;
        const newX = x - adjustStart.x;
        const newY = y - adjustStart.y;
        setSelection({
          ...selection,
          x: Math.max(0, Math.min(newX, canvas.width - selection.width)),
          y: Math.max(0, Math.min(newY, canvas.height - selection.height)),
        });
      } else {
        const newSelection = adjustRectangle(adjustHandle, deltaX, deltaY, selection);
        setSelection(newSelection);
        setAdjustStart({ x, y });
      }
    }

    updateCursor(x, y);
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
    setIsAdjusting(false);
    setAdjustHandle(null);
  };

  const handleCrop = () => {
    const canvas = canvasRef.current;
    const resultCanvas = resultCanvasRef.current;
    if (!image || !selection || !canvas || !resultCanvas || selection.width <= 0 || selection.height <= 0) return;
  
    // Coordinates and size in original image pixels
    const x = Math.max(0, Math.min(selection.x, canvas.width));
    const y = Math.max(0, Math.min(selection.y, canvas.height));
    const width = Math.min(selection.width, canvas.width - x);
    const height = Math.min(selection.height, canvas.height - y);
  
    if (width <= 0 || height <= 0) return;
  
    // Set result canvas to EXACT cropped size (full resolution)
    resultCanvas.width = width;
    resultCanvas.height = height;
    resultCanvas.x=x;
    resultCanvas.y=y;

    const resultCtx = resultCanvas.getContext("2d");
    resultCtx.clearRect(0, 0, width, height);
  
    // Draw directly from original image at full quality
    resultCtx.drawImage(image, x, y, width, height, 0, 0, width, height);
  
    // Show real cropped dimensions
    setImageDimensions(`${width} × ${height} px`);
    const type = detectImageType(resultCanvas);
    setImageType(type);
    setShowResult(true);
  
    // Scroll to result
    setTimeout(() => {
      document.getElementById("resultSection")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 100);
  
    // Pass the full-res canvas
    console.log(imageType)
    resultCanvas.color = imageType === "Color";
    setCroppedCanvas(resultCanvas);
    setOpenEditModal(true);
  };

  const handleClearSelection = () => {
    setSelection(null);
    setShowResult(false);
    setImageDimensions("-");
    setFirstPoint(null);
    setImageType("-");
  };

  const handleReset = () => {
    setImage(null);
    setSelection(null);
    setIsDrawing(false);
    setIsAdjusting(false);
    setShowResult(false);
    setImageDimensions("-");
    setImageType("-");
    setFirstPoint(null);
    const canvas = canvasRef.current;
    const resultCanvas = resultCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    if (resultCanvas) {
      const ctx = resultCanvas.getContext("2d");
      ctx.clearRect(0, 0, resultCanvas.width, resultCanvas.height);
    }
  };
  // Add this function inside your component
  const getCroppedBlob = useCallback((mimeType = "image/png", quality = 0.92) => {
    return new Promise((resolve, reject) => {
      const resultCanvas = resultCanvasRef.current;
      if (!resultCanvas) {
        reject(new Error("No cropped canvas available"));
        return;
      }

      resultCanvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to create blob"));
          }
        },
        mimeType,
        quality
      );
    });
  }, []);
  const handleDownload = () => {
    const resultCanvas = resultCanvasRef.current;
    if (!resultCanvas) return;

    const link = document.createElement("a");
    link.download = "cropped-ad.png";
    link.href = resultCanvas.toDataURL();
    link.click();
  };
  
  return (
    <div className="w-full mx-auto p-4 space-y-4 relative">
      {/* Controls */}
      <div className="flex flex-wrap gap-4 flex-col items-center fixed top-1/2 right-4 text-[20px] bg-[#b7b7b7] py-4 px-2 rounded-lg">
        <sl-tooltip content="Next">
          <button className={`hover:bg-[#515151] text-xs font-semibold rounded hover:text-white p-1`} onClick={() => {handleNext(); handleClearSelection()}}>Next</button>
        </sl-tooltip><sl-tooltip content="Previous">
          <button className={`hover:bg-[#515151] text-xs font-semibold rounded hover:text-white p-1`} onClick={()=> {handlePrevious(); handleClearSelection()}}>Prev</button>
        </sl-tooltip>
        <sl-tooltip content="Manual Mode">
          <SlIcon library="material" name="tune" className={`hover:bg-[#515151] p-2 rounded hover:text-white ${manualMode ? "bg-[#515151] text-white" : ""} `} onClick={() => { setManualMode((prev) => !prev); handleClearSelection()}}></SlIcon>
        </sl-tooltip>
        <sl-tooltip content="Debug">
          <SlIcon library="lucide" name="bug" className={`hover:bg-[#515151] p-2 rounded hover:text-white ${debugMode ? "bg-[#515151] text-white" : ""} `} onClick={() => setDebugMode((prev) => !prev)}></SlIcon>
        </sl-tooltip>
        <sl-tooltip content="crop">
          <SlIcon onClick={handleCrop} disabled={!image || !selection} library="lucide" className={`hover:bg-[#515151] p-2 rounded hover:text-white `} name="crop"></SlIcon>
        </sl-tooltip>
        <sl-tooltip content="clear selection">
          <SlIcon onClick={handleClearSelection} disabled={!selection} library="lucide" name="trash" className={`hover:bg-[#515151] p-2 rounded hover:text-white `}></SlIcon>
        </sl-tooltip>
      </div>

      {/* Detection sliders */}
      <div className="grid md:grid-cols-3 gap-4 text-sm bg-slate-900/40 border border-slate-700 rounded p-3">
        <div>
          <label htmlFor="whiteThreshold" className="flex justify-between mb-1">
            <span>White Threshold</span>
            <span>{detectionParams.whiteThreshold}</span>
          </label>
          <input
            id="whiteThreshold"
            type="range"
            min="180"
            max="255"
            value={detectionParams.whiteThreshold}
            onChange={(e) =>
              setDetectionParams((prev) => ({
                ...prev,
                whiteThreshold: parseInt(e.target.value),
              }))
            }
            className="w-full"
          />
        </div>
        <div>
          <label htmlFor="searchDistance" className="flex justify-between mb-1">
            <span>Max Search Distance (%)</span>
            <span>{detectionParams.maxSearchDistancePercent}</span>
          </label>
          <input
            id="searchDistance"
            type="range"
            min="10"
            max="100"
            value={detectionParams.maxSearchDistancePercent}
            onChange={(e) =>
              setDetectionParams((prev) => ({
                ...prev,
                maxSearchDistancePercent: parseInt(e.target.value),
              }))
            }
            className="w-full"
          />
        </div>
        <div>
          <label htmlFor="scanRange" className="flex justify-between mb-1">
            <span>Scan Range (px)</span>
            <span>{detectionParams.scanRange}</span>
          </label>
          <input
            id="scanRange"
            type="range"
            min="10"
            max="200"
            value={detectionParams.scanRange}
            onChange={(e) =>
              setDetectionParams((prev) => ({
                ...prev,
                scanRange: parseInt(e.target.value),
              }))
            }
            className="w-full"
          />
        </div>
      </div>

      {/* Main canvas */}
      <div>
        <div className="border border-slate-700 rounded overflow-hidden bg-slate-900">
          <canvas ref={canvasRef} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} style={{ cursor }} className="w-full h-full block" />
        </div>

        {/* Debug panel */}
        {debugMode && (
          <div className="text-xs bg-slate-900 border border-slate-700 rounded p-3 space-y-2 mt-4">
            <div>
              <span className="font-semibold text-emerald-400">Detection status:</span> <span style={{ color: "white" }}>{detectionStatus}</span>
            </div>
            <div>
              <span className="font-semibold text-emerald-400">Borders found:</span> <span style={{ color: "white" }}>{bordersFound}</span>
            </div>
          </div>
        )}
      </div>

      {/* Result section */}
      <div className="flex gap-6 w-full mt-4">
        {/* LEFT SIDE — RESULT SECTION */}
        {/* <div id="resultSection" style={{ display: showResult ? "flex" : "none" }} className="w-1/2 max-h-[700px] border border-slate-700 bg-slate-900/50 rounded p-3 space-y-3 flex-col"> */}
          {/* <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="space-x-3">
              <span className="font-semibold">Dimensions:</span>
              <span>{imageDimensions}</span>
            </div>
            <div className="space-x-3">
              <span className="font-semibold">Type:</span>
              <span
                style={{
                  color: imageType === "Color" ? "#e74c3c" : "#2c3e50",
                  fontWeight: "bold",
                }}
              >
                {imageType}
              </span>
            </div>
            <button onClick={handleDownload} className="px-3 py-1.5 rounded bg-sky-600 text-white text-sm">
              Download cropped
            </button>
          </div> */}
          <div className="flex-1 flex items-center justify-center overflow-hidden border border-slate-700 rounded bg-slate-950">
            <canvas ref={resultCanvasRef} className="hidden" />
          </div>
        {/* </div> */}
        {/*  FROM TO UPLOAD */}
        {/* <div className="w-1/2 max-h-[700px] border border-slate-700 rounded p-4 flex items-start justify-center">
          <div className="p-4 bg-white rounded shadow max-w-md">
            {Object.entries(filterData).map(([key, val]) => {
              if (!val) return null;
              const display =
                typeof val === "object" && val.label ? (
                  <>
                    {val.label}
                    {val.logo && <img src={val.logo} alt="logo" className="inline w-6 h-6 ml-2" />}
                  </>
                ) : (
                  val
                );
              return (
                <div key={key} className="mb-2">
                  <span className="font-semibold capitalize">{key}:</span> <span>{display}</span>
                </div>
              );
            })}
          </div>
        </div> */}
      </div>
    </div>
  );
}